/**
 * Creates a cross-origin-isolated Turso leader worker from same-origin assets.
 *
 * EAS Hosting's static asset responses do not inherit the route-level COEP
 * headers. Loading either public module URL directly from an isolated document
 * is therefore blocked by Chromium. Ordinary same-origin `fetch` still works,
 * and Blob workers inherit the creator document's isolated agent cluster, so the
 * page materializes both native modules as Blob URLs instead.
 */
import pMemoize from "@-/com.npmjs.p-memoize";
import { fetchWithDeadline, withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import {
  getTursoLeaderWorkerAssetUrl,
  getTursoSyncWasmAssetUrl,
  TURSO_LEADER_RESOURCE_ID_GLOBAL_KEY,
  TURSO_SYNC_WASM_MODULE_URL_GLOBAL_KEY,
} from "#turso-web-worker-assets";

interface TursoWorkerRuntimeAssets {
  leaderWorkerSource: string;
  syncWasmModuleUrl: string;
}

export interface TursoLeaderWorkerHandle {
  dispose(): void;
  worker: Worker;
}

/**
 * The asset server must start answering within this long. Same-origin static assets normally
 * answer in milliseconds; this is the ordinary single-request budget.
 */
const ASSET_HEADERS_DEADLINE_MS = 20_000;

/**
 * The download may go this long WITHOUT RECEIVING A BYTE before it is declared hung. The deadline
 * restarts on every chunk, so a slow-but-moving 16 MB sync-wasm download on a poor connection is
 * never cut off, while a stalled one ends promptly instead of pinning every database open.
 */
const ASSET_STALL_DEADLINE_MS = 20_000;

/**
 * Absolute ceiling on one asset download, stalls or not: a server trickling a byte every few
 * seconds would otherwise keep restarting the stall deadline forever. 16 MB at ~0.5 Mbit/s takes
 * ~4 minutes; nothing that could ever finish takes longer than this.
 */
const ASSET_TOTAL_DEADLINE_MS = 5 * 60_000;

/** Read a streamed body to text, failing when no chunk arrives within the stall deadline. */
async function readTextWithStallDeadline(response: Response, label: string): Promise<string> {
  const body = response.body;
  if (body === null) {
    return "";
  }
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parts: string[] = [];
  try {
    while (true) {
      // One deadline per chunk: this is the "restart on progress" bound.
      // oxlint-disable-next-line no-await-in-loop -- chunks of one stream must be read in order
      const chunk = await withDeadline(
        {
          deadline: ASSET_STALL_DEADLINE_MS,
          operation: `download of the ${label} asset (no progress)`,
        },
        async () => reader.read(),
      );
      if (chunk.done) {
        break;
      }
      parts.push(decoder.decode(chunk.value, { stream: true }));
    }
  } catch (error) {
    // Stop the transfer itself, not just our wait for it.
    void reader.cancel(error).catch(() => {});
    throw error;
  }
  parts.push(decoder.decode());
  return parts.join("");
}

async function fetchJavaScriptSource(url: string, label: string): Promise<string> {
  return withDeadline(
    { deadline: ASSET_TOTAL_DEADLINE_MS, operation: `download of the ${label} asset` },
    async (signal) => {
      const response = await fetchWithDeadline(url, {
        // The body is streamed and bounded below by the stall deadline instead of being buffered
        // under one fixed deadline, which a legitimately slow 16 MB download could outlast.
        bufferBody: false,
        credentials: "same-origin",
        deadline: ASSET_HEADERS_DEADLINE_MS,
        operation: `request for the ${label} asset`,
        // The total deadline aborts the transfer too, including a body read in progress.
        signal,
      });
      if (!response.ok) {
        void response.body?.cancel().catch(() => {});
        throw new Error(
          `Unable to load the ${label} asset at ${url}: ${response.status} ${response.statusText}`,
        );
      }
      return readTextWithStallDeadline(response, label);
    },
  );
}

/**
 * Fetch once per page. The 16 MB sync-wasm bundle is shared by the independent
 * guest and actor workers instead of being downloaded and copied twice.
 *
 * Only a SUCCESSFUL load is memoized: `pMemoize` drops a rejected promise, so a download that
 * failed or hit one of the deadlines above is retried by the next open instead of being handed to
 * every later open. Before those deadlines a hung download was the one outcome that WAS cached —
 * pending forever, so every later database open in the page hung behind it.
 *
 * The object URL deliberately lives for the page lifetime. Revoking it when one
 * database closes would break the other database's worker or a later reopen.
 * The browser releases it with the document.
 */
const loadTursoWorkerRuntimeAssets = pMemoize(async (): Promise<TursoWorkerRuntimeAssets> => {
  const [leaderWorkerSource, syncWasmSource] = await Promise.all([
    fetchJavaScriptSource(getTursoLeaderWorkerAssetUrl(), "Turso leader worker"),
    fetchJavaScriptSource(getTursoSyncWasmAssetUrl(), "Turso sync-wasm module"),
  ]);
  const syncWasmModuleUrl = URL.createObjectURL(
    new Blob([syncWasmSource], { type: "text/javascript" }),
  );
  return { leaderWorkerSource, syncWasmModuleUrl };
});

/** Build the tiny prelude that configures the fetched leader bundle. */
export function createTursoLeaderWorkerBootstrapSource(
  resourceId: string,
  syncWasmModuleUrl: string,
): string {
  const resourceAssignment = `Reflect.set(globalThis, ${JSON.stringify(TURSO_LEADER_RESOURCE_ID_GLOBAL_KEY)}, ${JSON.stringify(resourceId)});`;
  const syncWasmAssignment = `Reflect.set(globalThis, ${JSON.stringify(TURSO_SYNC_WASM_MODULE_URL_GLOBAL_KEY)}, ${JSON.stringify(syncWasmModuleUrl)});`;
  return `${resourceAssignment}\n${syncWasmAssignment}`;
}

export async function createTursoLeaderWorker(
  resourceId: string,
): Promise<TursoLeaderWorkerHandle> {
  const { leaderWorkerSource, syncWasmModuleUrl } = await loadTursoWorkerRuntimeAssets();
  const bootstrapSource = createTursoLeaderWorkerBootstrapSource(resourceId, syncWasmModuleUrl);
  const leaderWorkerUrl = URL.createObjectURL(
    new Blob([bootstrapSource, "\n", leaderWorkerSource], { type: "text/javascript" }),
  );

  let worker: Worker;
  try {
    worker = new Worker(leaderWorkerUrl, { type: "module" });
  } catch (error) {
    URL.revokeObjectURL(leaderWorkerUrl);
    throw error;
  }

  let disposed = false;
  return {
    dispose: () => {
      if (disposed) {
        return;
      }
      disposed = true;
      worker.terminate();
      URL.revokeObjectURL(leaderWorkerUrl);
    },
    worker,
  };
}
